import React, { createContext, useContext, useState, PropsWithChildren, useEffect } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { isSameDay, addDays } from 'date-fns';
import { AllocationType, Quantity } from '@guest-widgets/shared/src/types/product/miscellaneous';
import { GuestProduct, GuestType } from '@guest-widgets/shared/src/types/product/guestProduct';
import { UpsellItem } from '@guest-widgets/shared/src/types/product/upsellItem';
import { CartItem } from '@guest-widgets/shared/src/types/product/cartItem';
import { useQueryRouter } from '@guest-widgets/shared/src/components/Router/QueryRouter';
import { useTimeslots } from '../../contexts/timeslotsContext/timeslotsContext';

import type { TimeslotDayRange } from '../Steps/Day/Calendar/CalendarRange/useCalendarRange';
import type { TimeRange as BaseTimeRange } from '../../contexts/timeslotsContext/timeslots';
import { mapToQuantity } from '../../contexts/apiContext/product/mapToPricing';
import { formatDate } from '../../contexts/apiContext/product/formatDate';
import { usePricingApi } from '../../contexts/apiContext/product/usePricingApi';
import type { Pricing } from '../../contexts/apiContext/product/product';
import { useUpsellItemsFromCart } from '../../hooks/upsell/useUpsellItemsFromCart';
import { TimeslotRules } from '../../contexts/apiContext/timeslot/dtos/response';

export type TimeRange = Partial<BaseTimeRange>;

interface GuestProductInSelectionContext extends GuestProduct {
  /** Indicates whether all information needed in order to add the product into the cart has being provided */
  isComplete: boolean;
  /** Indicates whether all information needed in order to activate quantity selector */
  isDateTimeSelectionComplete: boolean;
  /** Indicates whether the product requires a date range selection */
  requiresDateRange: boolean;
  /** Indicates whether the product requires a time selection */
  requiresTimeSelection: boolean;
  time?: TimeRange;
  date?: TimeslotDayRange;
  quantity?: Quantity;
  pricingApi: UseQueryResult<Pricing>;
  setDate: (date?: TimeslotDayRange) => void;
  setTime: (time?: TimeRange) => void;
  setQuantity: (quantity?: Quantity) => void;
  quantityChanged?: boolean;
  setQuantityChanged?: React.Dispatch<React.SetStateAction<boolean>>;
}

const GuestProductInSelectionContext = createContext({} as GuestProductInSelectionContext);

export interface GuestRate extends GuestType {
  rate?: number;
}

export type ProductProp = GuestProduct | UpsellItem;

interface ProductInSelectionProps {
  product: ProductProp;
}

const getHiddenGuestTypes = (rules: TimeslotRules, guestTypes: GuestType[]): GuestType[] => {
  if (!rules?.guestTypeRestrictions) {
    return [];
  }

  return guestTypes.filter((guestType) => {
    const restriction = rules.guestTypeRestrictions.find(
      (restriction) => restriction.guestTypeId === guestType.id
    );

    if (!restriction) {
      return false;
    }

    // Hide guest type if maxGuests is 0 or minGuests is greater than maxGuests
    return (
      restriction.maxGuests === 0 ||
      (restriction.minGuests !== null &&
        restriction.maxGuests !== null &&
        restriction.minGuests > restriction.maxGuests)
    );
  });
};

export const ProductInSelectionProvider = ({
  product,
  children,
}: PropsWithChildren<ProductInSelectionProps>) => {
  const { allocationType, productId, name, allowBookingMultiDays } = product;
  const query = useQueryRouter();
  const { timeslot } = useTimeslots();

  const initialDateFrom = query.get('re-filter-date-from');
  const initialDateTo = query.get('re-filter-date-to');

  const parsedDateFrom = initialDateFrom ? new Date(initialDateFrom) : undefined;
  const parsedDateTo = initialDateTo ? new Date(initialDateTo) : undefined;

  const [date, setDate] = useState<TimeslotDayRange>({});

  // Populate empty availabilityTimes when timeslot data is available
  useEffect(() => {
    if (!timeslot?.data?.days || !date?.from?.day) return;

    let shouldUpdate = false;
    let newFrom = date.from;
    let newTo = date.to;

    // Check and fill for 'from'
    if (date.from && date.from.day && date.from.availabilityTimes?.length === 0) {
      const fromTimeslotDay = timeslot.data.days.find((day) => isSameDay(day.day, date!.from!.day));
      const fromTimes = fromTimeslotDay?.availabilityTimes;
      if (fromTimes && fromTimes.length > 0) {
        newFrom = {
          ...date.from,
          day: date.from.day,
          availabilityTimes: fromTimes,
        };
        shouldUpdate = true;
      }
    }

    // Check and fill for 'to' if present
    if (date.to && date.to.day && date.to.availabilityTimes?.length === 0) {
      const toTimeslotDay = timeslot.data.days.find((day) => isSameDay(day.day, date!.to!.day));
      const toTimes = toTimeslotDay?.availabilityTimes;
      if (toTimes && toTimes.length > 0) {
        newTo = {
          ...date.to,
          day: date.to.day,
          availabilityTimes: toTimes,
        };
        shouldUpdate = true;
      }
    }

    if (shouldUpdate) {
      setDate((prev) => ({
        ...prev,
        from: newFrom,
        to: newTo,
      }));
    }
  }, [
    timeslot?.data?.days,
    date?.from?.day,
    date?.from?.availabilityTimes,
    date?.to?.day,
    date?.to?.availabilityTimes,
  ]);

  // Set initial dates from URL parameters
  useEffect(() => {
    // Only set if date is empty (no from and no to)
    if (parsedDateFrom && parsedDateTo && !date.from && !date.to) {
      setDate({
        from: { day: parsedDateFrom, availabilityTimes: [] },
        to: { day: parsedDateTo, availabilityTimes: [] },
        isCalendarSelection: true,
        isAvailable: true,
      });
    }
  }, [parsedDateFrom, parsedDateTo, date.from, date.to]);

  const [time, setTime] = useState<TimeRange>();
  const [quantityChanged, setQuantityChanged] = useState<boolean>(false);
  const [quantity, setQuantity] = useState<Quantity>();

  const flextimeMultiDays = allowBookingMultiDays && allocationType === 'flextime';
  const requiresDateRange = ['day', 'night'].includes(allocationType) || flextimeMultiDays;
  const requiresTimeSelection =
    ['timeslot', 'flextime'].includes(allocationType) || flextimeMultiDays;
  const endDateRange =
    allocationType === 'day' && date?.to?.day ? addDays(date.to.day, 1) : date?.to?.day;

  const isDateTimeSelectionComplete = (() => {
    const mapper: Record<AllocationType, boolean> = {
      day: Boolean(date?.from && (date.isAvailable ?? true)),
      night: Boolean(
        date?.from &&
          date.to &&
          !isSameDay(date.from.day, date.to.day) &&
          (date.isAvailable ?? true)
      ),
      flextime: Boolean(
        date?.from && date.to && !date.isCalendarSelection && (date.isAvailable ?? true)
      ),
      timeslot: Boolean(date?.from && date?.to && time?.startTime),
    };

    return mapper[allocationType];
  })();

  const isComplete = isDateTimeSelectionComplete && isQuantitySelected(quantity);

  // Determine visible guests based on rules
  const rules = date?.from?.availabilityTimes?.[0]?.rules as TimeslotRules;
  const hiddenGuests = getHiddenGuestTypes(rules, product.guestTypes);
  const visibleGuests = product.guestTypes.filter(
    (guestType) => !hiddenGuests.some((hiddenGuest) => hiddenGuest.id === guestType.id)
  );

  // Create a new product object with only visible guest types
  const filteredProduct = {
    ...product,
    guestTypes: visibleGuests,
  };

  const guestTypes = mapToQuantity(filteredProduct.guestTypes);
  const experience = {
    id: isUpsell(product) ? product.id : undefined,
    product: { productId, name },
    guestTypes,
    start: formatDate(date?.from?.day),
    end: formatDate(endDateRange),
  } as CartItem;

  const pricingApi = usePricingApi({
    experience,
    quantity,
    guestTypesWithQuantity: filteredProduct.guestTypes,
    isDateTimeSelectionComplete,
  });

  const setDateHandler = (value: TimeslotDayRange | undefined) => {
    if (!value || typeof value === 'function') return;

    if (requiresDateRange) {
      return setDate({ ...value, isCalendarSelection: true });
    }

    setDate(value);
  };

  const setTimeHandler = (value: TimeRange | undefined) => {
    if (!value || typeof value === 'function') return;

    setTime(value);

    if (date && value?.startTime && value?.endTime)
      setDate(mapToDateRange(date, value.startTime, value.endTime));
  };

  useUpsellItemsFromCart({
    upsell: filteredProduct,
    setDateHandler,
    setTimeHandler,
    setQuantity,
    time,
    requiresTimeSelection,
  });

  const value: GuestProductInSelectionContext = {
    ...filteredProduct,
    date,
    setDate: setDateHandler,
    time,
    setTime: setTimeHandler,
    quantity,
    setQuantity,
    isComplete,
    isDateTimeSelectionComplete,
    requiresDateRange,
    requiresTimeSelection,
    pricingApi,
    quantityChanged,
    setQuantityChanged,
  };

  return (
    <GuestProductInSelectionContext.Provider value={value}>
      {children}
    </GuestProductInSelectionContext.Provider>
  );
};

export const useProductInSelection = () => {
  const context = useContext(GuestProductInSelectionContext);
  if (context === undefined) {
    throw new Error('useProductInSelection must be used within a GuestProductInSelectionProvider');
  }
  return context;
};

const isQuantitySelected = (quantity?: Quantity) =>
  Object.values(quantity || {}).reduce((a, b) => a + b, 0) > 0;

const mapToDateRange = (
  date: TimeslotDayRange,
  startTime: Date,
  endTime: Date
): TimeslotDayRange => {
  const from = {
    ...date.from,
    day: startTime,
    availabilityTimes: date.from?.availabilityTimes || [],
  };
  const to = {
    ...date.to,
    day: endTime,
    availabilityTimes: date.to?.availabilityTimes || [],
  };

  return { ...date, from, to, isCalendarSelection: false };
};

export const isUpsell = (product: ProductProp): product is UpsellItem => {
  return !!(product as UpsellItem)?.id?.includes('.');
};
